import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Download, Send, Trash2, EyeOff, AlertCircle, ExternalLink, Loader } from 'lucide-react';
import { listNFTs, type NFTItem } from '../../../../api/nft';
import { CHAIN_NAME } from '@/utils/chain';

interface NFTGalleryProps {
  petId: string;
  onClose: () => void;
}

interface NFT {
  id: string;
  petName: string;
  artwork: string;
  frame: string;
  background: string;
  caption: string;
  mintDate: string;
  network: string;
  tokenId: string;
  hidden: boolean;
  imageUrl: string | null;
  explorerUrl: string;
}

function mapApiNft(item: NFTItem): NFT {
  return {
    id: item.id,
    petName: item.name,
    artwork: item.artworkType,
    frame: item.frame || '',
    background: item.background || '',
    caption: item.caption || '',
    mintDate: item.createdAt,
    network: item.network || 'Solana',
    tokenId: item.nftMintAddress,
    hidden: item.hidden,
    imageUrl: item.imageUrl,
    explorerUrl: item.explorerUrl,
  };
}

export function NFTGallery({ petId, onClose }: NFTGalleryProps) {
  const { t } = useTranslation();
  const [nfts, setNfts] = useState<NFT[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedNFT, setSelectedNFT] = useState<NFT | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function fetchNfts() {
      try {
        setLoading(true);
        setError(null);
        const response = await listNFTs(petId);
        if (!cancelled) {
          setNfts(response.nfts.map(mapApiNft));
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to fetch NFTs:', err);
          setError('Failed to load NFTs. Please try again.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    fetchNfts();
    return () => { cancelled = true; };
  }, [petId]);

  const visibleNFTs = nfts.filter(nft => !nft.hidden);

  const handleHide = (nftId: string) => {
    setNfts(nfts.map(nft => nft.id === nftId ? { ...nft, hidden: true } : nft));
    setSelectedNFT(null);
  };

  const handleBurn = (nftId: string) => {
    if (confirm(t('nft.gallery.burnConfirm'))) {
      setNfts(nfts.filter(nft => nft.id !== nftId));
      setSelectedNFT(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-pink-500 to-purple-500 text-white p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl mb-1">{t('nft.gallery.title')}</h2>
              <p className="text-sm opacity-90">{t('nft.gallery.subtitle')}</p>
            </div>
            <button onClick={onClose} className="text-white/80 hover:text-white">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-6">
          {/* Important Notice */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-yellow-800">
              {t('nft.gallery.rememberNotice')}
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="text-center py-12">
              <Loader className="w-10 h-10 text-pink-600 animate-spin mx-auto mb-4" />
              <p className="text-gray-600">{t('nft.gallery.loading', 'Loading your NFTs...')}</p>
            </div>
          )}

          {/* Error State */}
          {!loading && error && (
            <div className="text-center py-12">
              <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">{error}</p>
              <button
                onClick={() => {
                  setLoading(true);
                  setError(null);
                  listNFTs(petId)
                    .then(res => setNfts(res.nfts.map(mapApiNft)))
                    .catch(() => setError('Failed to load NFTs.'))
                    .finally(() => setLoading(false));
                }}
                className="px-6 py-2 bg-pink-500 text-white rounded-lg hover:opacity-90"
              >
                {t('common.retry', 'Retry')}
              </button>
            </div>
          )}

          {/* NFT Grid */}
          {!loading && !error && visibleNFTs.length > 0 && (
            <div className="grid md:grid-cols-3 gap-6">
              {visibleNFTs.map(nft => (
                <div
                  key={nft.id}
                  onClick={() => setSelectedNFT(nft)}
                  className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                >
                  {/* NFT Preview */}
                  <div className="aspect-square relative overflow-hidden">
                    {nft.imageUrl ? (
                      <img
                        src={nft.imageUrl}
                        alt={nft.petName}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className={`w-full h-full p-6 ${
                        nft.background === 'gradient-blue' ? 'bg-gradient-to-br from-blue-400 to-purple-400' :
                        nft.background === 'gradient-pink' ? 'bg-gradient-to-br from-pink-400 to-orange-400' :
                        nft.background === 'gradient-green' ? 'bg-gradient-to-br from-green-400 to-teal-400' :
                        'bg-gradient-to-br from-gray-400 to-gray-600'
                      }`}>
                        <div className="w-full h-full rounded-lg bg-white flex items-center justify-center">
                          <div className="text-center">
                            <div className="text-5xl mb-3">🐕</div>
                            <div className="mb-1">{nft.petName}</div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* NFT Info */}
                  <div className="p-4 border-t border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-sm truncate">{nft.petName}</div>
                        <div className="font-mono text-xs text-gray-500 truncate">{nft.tokenId.slice(0, 12)}...</div>
                      </div>
                      <div className="px-2 py-1 bg-pink-50 text-pink-600 rounded text-xs flex-shrink-0 ml-2">
                        {t('nft.gallery.funOnly')}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500">
                      {t('nft.gallery.minted', { date: new Date(nft.mintDate).toLocaleDateString() })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && visibleNFTs.length === 0 && (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">✨</div>
              <h3 className="text-xl mb-2">{t('nft.gallery.noNftsYet')}</h3>
              <p className="text-gray-600 mb-6">{t('nft.gallery.mintFirstPrompt')}</p>
              <button
                onClick={onClose}
                className="px-6 py-3 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-lg hover:opacity-90"
              >
                {t('nft.gallery.mintFunNft')}
              </button>
            </div>
          )}
        </div>

        {/* NFT Detail Modal */}
        {selectedNFT && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedNFT(null)}>
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
              <div className="p-6">
                <div className="flex justify-between items-start mb-6">
                  <h3 className="text-2xl">{t('nft.gallery.detailsTitle')}</h3>
                  <button onClick={() => setSelectedNFT(null)} className="text-gray-400 hover:text-gray-600">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* NFT Preview */}
                  <div className="aspect-square rounded-xl overflow-hidden">
                    {selectedNFT.imageUrl ? (
                      <img
                        src={selectedNFT.imageUrl}
                        alt={selectedNFT.petName}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className={`w-full h-full p-6 ${
                        selectedNFT.background === 'gradient-blue' ? 'bg-gradient-to-br from-blue-400 to-purple-400' :
                        selectedNFT.background === 'gradient-pink' ? 'bg-gradient-to-br from-pink-400 to-orange-400' :
                        selectedNFT.background === 'gradient-green' ? 'bg-gradient-to-br from-green-400 to-teal-400' :
                        'bg-gradient-to-br from-gray-400 to-gray-600'
                      }`}>
                        <div className="w-full h-full rounded-lg bg-white flex items-center justify-center">
                          <div className="text-center">
                            <div className="text-6xl mb-4">🐕</div>
                            <div className="text-xl mb-2">{selectedNFT.petName}</div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* NFT Info */}
                  <div className="space-y-4">
                    <div className="bg-pink-50 border border-pink-200 rounded-lg px-3 py-2 text-center">
                      <span className="text-sm text-pink-800">{t('nft.gallery.funOnlyNoUtility')}</span>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <div className="text-sm text-gray-600">{t('nft.gallery.petName')}</div>
                        <div>{selectedNFT.petName}</div>
                      </div>
                      {selectedNFT.caption && (
                        <div>
                          <div className="text-sm text-gray-600">{t('nft.gallery.caption')}</div>
                          <div>"{selectedNFT.caption}"</div>
                        </div>
                      )}
                      <div>
                        <div className="text-sm text-gray-600">{t('nft.gallery.network')}</div>
                        <div>{selectedNFT.network}</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-600">{t('nft.gallery.tokenId')}</div>
                        <div className="font-mono text-xs break-all">{selectedNFT.tokenId}</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-600">{t('nft.gallery.mintDate')}</div>
                        <div>{new Date(selectedNFT.mintDate).toLocaleString()}</div>
                      </div>
                    </div>

                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                      {t('nft.gallery.noticeNotLinked')}
                    </div>

                    <div className="space-y-2">
                      <a
                        href={selectedNFT.explorerUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                      >
                        <ExternalLink className="w-4 h-4" />
                        {t('nft.gallery.viewOnSolana', { chain: CHAIN_NAME })}
                      </a>
                      {selectedNFT.imageUrl && (
                        <a
                          href={selectedNFT.imageUrl}
                          download={`${selectedNFT.petName}.png`}
                          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                        >
                          <Download className="w-4 h-4" />
                          {t('nft.gallery.downloadImage')}
                        </a>
                      )}
                      <button className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors">
                        <Send className="w-4 h-4" />
                        {t('nft.gallery.transferNft')}
                      </button>
                      <button
                        onClick={() => handleHide(selectedNFT.id)}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                      >
                        <EyeOff className="w-4 h-4" />
                        {t('nft.gallery.hideFromGallery')}
                      </button>
                      <button
                        onClick={() => handleBurn(selectedNFT.id)}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                        {t('nft.gallery.burnNft')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
